if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }
      connectedCallback() {
        this.init();
      }
      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();
        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();
        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }
      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          observer: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };
        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: { slidesPerView: 5 },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };
        if (this.mediaLayout === 'vertical-carousel') {
          this.thumbsOptions.breakpoints[768] = {
            direction: 'vertical',
            slidesPerView: 'auto',
          };
        }
      }
      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile || this.enableDesktopSlider) {
          this.initSlider();
        } else {
          this.destroySlider();
        }
      }
      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();
            this.sliderOptions.thumbs = { swiper: this.thumbsInstance.slider };
          }
          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [FoxTheme.Swiper.Thumbs]);
          this.sliderInstance.init();
          this.handleSliderAfterInit();
          this.handleSlideChange();
          this.sliderInstance.slider.init();

          document.addEventListener('variant:changed', (event) => {
            const variant = event.detail.variant;
            if (!variant) return;

            this.setAttribute('loading', 'true');
            if(this.elements.viewer) this.elements.viewer.style.opacity = '0.5';

            const imgUrl = variant.featured_image?.src;
            const rawMediaData = window.variantMediaMap[variant.id] || [];
            
            const matchedMedia = rawMediaData.map((item, index) => ({
              id: `${variant.id}-${index}`,
              src: item.path.split('?')[0] + '?width=1000',
              alt: item.alt,
              media_type: 'image',
              width: item.width || 1920,
              height: item.height || 1920,
            }));

            let activeIndex = 0;
            if (imgUrl) {
              const clean = (s) => s.split('?')[0];
              matchedMedia.forEach((m, i) => {
                if (clean(m.src) === clean(imgUrl)) activeIndex = i;
              });
            }

            this.preloadImages(matchedMedia).then(() => {
              this.updateGallerySlides(matchedMedia);
              this.updateThumbnails(matchedMedia, activeIndex);
              
              if (this.sliderInstance?.slider) {
                this.sliderInstance.slider.update();
                this.sliderInstance.slider.slideTo(activeIndex, 0);
              }
              this.setActiveThumbnail(activeIndex);
              this.rebuildZoomData();

              this.removeAttribute('loading');
              if(this.elements.viewer) this.elements.viewer.style.opacity = '1';
            });
          });
        }
      }

      preloadImages(mediaList) {
        return Promise.all(mediaList.map(media => new Promise(resolve => {
          const img = new Image();
          img.onload = img.onerror = resolve;
          img.src = media.src;
        })));
      }

      updateGallerySlides(matchedMedia) {
        if (!this.elements.mediaList) return;
        this.elements.mediaList.innerHTML = "";
        matchedMedia.forEach((media, index) => {
          const mediaItem = document.createElement("div");
          mediaItem.className = "product__media-item swiper-slide variant_bottom";
          mediaItem.dataset.mediaType = "image";
          mediaItem.dataset.mediaIndex = index;
          mediaItem.dataset.src = media.src;
          mediaItem.dataset.pswpWidth = media.width;
          mediaItem.dataset.pswpHeight = media.height;

          mediaItem.innerHTML = `
            <div class="product__media-container blocks-radius" style="--aspect-ratio: 1.0">
              <motion-element class="product__media media-wrapper loaded">
                <img src="${media.src}" alt="${media.alt || ''}" class="loaded" loading="eager" width="${media.width}" height="${media.height}">
              </motion-element>
              <span class="product__media-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-zoom" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"></path><path d="M16.4434 16.4453L20.9997 21.0016"></path></svg>
              </span>
              <button class="product__media-zoom btn--inherit js-photoswipe--zoom" type="button" data-media-index="${index}"></button>
            </div>
          `;
          this.elements.mediaList.appendChild(mediaItem);
        });
      }

      updateThumbnails(matchedMedia, activeIndex = 0) {
        if (!this.elements.thumbnails) return;
        const wrapper = this.elements.thumbnails.querySelector('.swiper-wrapper');
        if (!wrapper) return;
        wrapper.innerHTML = "";
        matchedMedia.forEach((media, index) => {
          const thumb = document.createElement("div");
          thumb.className = `product__thumbs-item swiper-slide ${index === activeIndex ? 'swiper-slide-thumb-active' : ''}`;
          thumb.innerHTML = `
            <div class="product__thumbs-container medium-blocks-radius media-wrapper loaded">
              <div class="product__thumbs-inner" style="--aspect-ratio: 1">
                <img src="${media.src}" width="150" height="150">
              </div>
            </div>
          `;
          thumb.onclick = () => {
            this.sliderInstance.slider.slideTo(index);
            this.setActiveThumbnail(index);
          };
          wrapper.appendChild(thumb);
        });
        if (this.thumbsInstance?.slider) this.thumbsInstance.slider.update();
      }

      setActiveThumbnail(index) {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll(".swiper-slide").forEach((t, i) => {
          t.classList.toggle("swiper-slide-thumb-active", i === index);
        });
      }

      initImageZoom() {
        this.rebuildZoomData();
        // Delegate click for zoom buttons
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, target) => {
            const index = parseInt(target.dataset.mediaIndex) || 0;
            if (this.lightbox) this.lightbox.loadAndOpen(index);
          },
        });
      }

      rebuildZoomData() {
        if (!this.enableImageZoom) return;
        const items = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const dataSource = items.map(media => ({
          src: media.dataset.src.split('?')[0] + '?width=2000',
          width: parseInt(media.dataset.pswpWidth) || 1920,
          height: parseInt(media.dataset.pswpHeight) || 1920,
        }));

        if (this.lightbox) this.lightbox.destroy();
        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 0.9,
          padding: { top: 20, bottom: 20, left: 20, right: 20 },
        });

        // Sync slider when zoom image changes
        this.lightbox.on('change', () => {
          const currIndex = this.lightbox.pswp.currIndex;
          if (this.sliderInstance?.slider) this.sliderInstance.slider.slideTo(currIndex);
        });

        this.lightbox.init();
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (s) => this.toggleSliderDraggableState(s.slides[s.activeIndex]?.dataset.mediaType !== 'model'));
      }
      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (s) => {
          if (s.thumbs.swiper) s.thumbs.swiper.slideTo(s.activeIndex);
          this.setActiveThumbnail(s.activeIndex);
        });
      }
      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider) this.sliderInstance.slider.allowTouchMove = isDraggable;
      }
    }
  );
}